import React from 'react';
import styled from 'styled-components';
import {Detail, Icon, Modal, Text} from '@forecast-it/design-system';
import {removeAllBody, removeAllLabel} from './featureFlagRemoval';
import {FEATURE_FLAG_DESTRUCTIVE_TEXT} from './featureFlagSurface';

const Body = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
`;

// The glyph keeps its own size while the prose beside it wraps, so it cannot be squeezed to nothing in
// a narrow dialog. Nudged down a couple of pixels to sit on the first line's cap height rather than
// above it.
//
// No colour here: unlike the typography components, the design system's Icon cannot inherit one. It
// defaults `color` to 'default', which is always truthy, so its styled svg always emits its own
// `path { fill: … }` and an inherited CSS colour never reaches the path. The colour has to be a prop.
const WarningGlyph = styled.span`
	display: flex;
	flex: none;
	margin-top: 2px;
`;

const Prose = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
`;

/* The typography component paints its own colour onto the element it renders, so the warning's red has
   to be declared on this wrapper's child rather than passed as a colour prop. */
const Irreversible = styled.span`
	> * {
		color: ${FEATURE_FLAG_DESTRUCTIVE_TEXT};
	}
`;

export interface FeatureFlagRemoveAllModalProps {
	/** Named in the question, because this dialog is reachable from any flag's detail page. */
	flagName: string;
	/** Every company listed in the assignment table, including those held only through named users. */
	assignedCount: number;
	/** True while the removal is in flight. */
	isRemoving: boolean;
	onClose: () => void;
	onConfirm: () => void;
}

/**
 * The confirmation for clearing every company off a flag.
 *
 * Both footer controls are blocked while the removal is in flight, for the same reasons as the bulk
 * bar: the mutation cannot be recalled, so a second confirm could only fail against grants that are
 * already gone, and Cancel would be offering an escape that no longer exists.
 */
export function FeatureFlagRemoveAllModal({
	flagName,
	assignedCount,
	isRemoving,
	onClose,
	onConfirm,
}: FeatureFlagRemoveAllModalProps): JSX.Element {
	return (
		// Interacting outside cannot dismiss this: it is the last checkpoint before an irreversible
		// removal, so leaving it has to be a deliberate press of Cancel.
		<Modal closeOnInteractOutside={false} onCloseModal={onClose} size={Modal.SIZE.SMALL}>
			<Modal.Title>Remove all companies from this flag?</Modal.Title>
			<Modal.Content>
				<Body>
					<WarningGlyph>
						{/* Decorative: the heading beside it already says this is destructive, so an
						    accessible name here would only repeat it. */}
						<Icon aria-hidden="true" icon="warning" newColor="error" size="m" />
					</WarningGlyph>
					<Prose>
						<Detail color="medium">{removeAllBody(assignedCount, flagName)}</Detail>
						<Irreversible>
							<Text>
								{
									'This can’t be undone. Everyone loses access to the feature immediately, and re-adding them means starting the rollout again.'
								}
							</Text>
						</Irreversible>
					</Prose>
				</Body>
			</Modal.Content>
			{/* Cancel sits in the tertiary slot, and its order here is what the footer's layout depends on.
			    The design system's footer is a plain left-packed flex row, and the tertiary slot is the only
			    one it wraps in a growing row — so that wrapper absorbing the slack is what pushes this pair
			    to the right of the dialog. Given as the secondary button instead, both controls end up
			    against the dialog's left edge. Cancel first then leaves the destructive control rightmost,
			    under the reader's cursor last. */}
			<Modal.Footer>
				<Modal.TertiaryFooterButton dataCy="feature-flag-remove-all-cancel" disabled={isRemoving} onPress={onClose}>
					Cancel
				</Modal.TertiaryFooterButton>
				<Modal.PrimaryFooterButton dataCy="feature-flag-remove-all-confirm" disabled={isRemoving} onPress={onConfirm}>
					{removeAllLabel(assignedCount)}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}
